/**
 * CAMP-2026-0907 — the app header must sit BELOW the Android status bar.
 *
 * The WebView draws under the status bar (`StatusBar.setOverlaysWebView
 * ({ overlay: true })` + MainActivity's edge-to-edge), so every app bar pads
 * its top by `max(env(safe-area-inset-top), <floor>)`. iOS reports the inset
 * through env() reliably. Android does only on a WebView new enough to pass
 * the insets through (Chromium 140+, which is also when Capacitor 8's
 * SystemBars plugin stops working around it); on older WebViews env() is 0
 * and the floor alone decides — and the floor was a fixed 28px. A phone whose
 * status bar is taller than that (punch-hole cameras, large display scaling:
 * 32–48dp is common) drew the dashboard's avatar over the clock and battery.
 *
 * The fix measures the real bar: the StatusBar plugin's `getInfo().height`
 * (dp = CSS px) becomes `--ch-status-bar-h` on <html>, and the app bars use it
 * as the floor in place of the fixed 28px (`var(--ch-status-bar-h, 28px)`, so
 * nothing changes until it is set).
 *
 * When NOT to set it: on Android 15+ with an older WebView, Capacitor's
 * SystemBars plugin pads the WebView itself below the status bar, so the web
 * content no longer starts under it — adding the bar's height there would
 * leave an empty band. Those devices keep today's floor. Unknown user agents
 * keep it too.
 */

/** The CSS custom property the app bars read (`native-brand-bar.css`, globals.css). */
export const STATUS_BAR_VAR = "--ch-status-bar-h";

/** First Android WebView (Chromium) major that passes window insets through to env(). */
const WEBVIEW_WITH_INSETS = 140;
/** Android 15 (API 35): from here Capacitor pads an old WebView below the bars. */
const ANDROID_PADS_OLD_WEBVIEW = 15;

/**
 * The status-bar floor in CSS px to publish, or null to leave the CSS
 * fallback (28px) alone. Pure so the decision is unit-tested.
 */
export function statusBarFloorPx(userAgent: string, heightDp: number): number | null {
  if (!Number.isFinite(heightDp) || heightDp <= 0 || heightDp > 120) return null;
  const android = /Android (\d+)/.exec(userAgent);
  if (!android) return null;
  const androidMajor = Number(android[1]);
  const chrome = /Chrome\/(\d+)/.exec(userAgent);
  const webviewMajor = chrome ? Number(chrome[1]) : 0;
  // A new WebView reports env() correctly; the measured height agrees with it,
  // so publishing it is harmless and covers the moment before insets arrive.
  if (webviewMajor >= WEBVIEW_WITH_INSETS) return Math.round(heightDp);
  // Old WebView on Android 15+: the WebView already starts below the bar.
  if (androidMajor >= ANDROID_PADS_OLD_WEBVIEW) return null;
  // Old WebView on Android ≤14: content is under the bar and env() says 0.
  return Math.round(heightDp);
}

/** Write (or clear) the floor on <html>. */
export function applyStatusBarFloor(px: number | null, root: HTMLElement = document.documentElement): void {
  if (px === null) root.style.removeProperty(STATUS_BAR_VAR);
  else root.style.setProperty(STATUS_BAR_VAR, `${px}px`);
}
